import { useCallback, useRef } from 'react';

import {
  LeadAploraCallReviewQuery,
  LeadAploraCardQuery,
  useDraftLeadAploraFollowupMutation,
  useLeadAploraCallReviewQuery,
  useLeadAploraCallTextLazyQuery,
  useLeadAploraCardQuery,
} from 'core/graphql';

import { newSmsRequestKey } from '../Sms/smsModel';
import {
  AploraErrorCode,
  aploraErrorCode,
  aploraQueryErrorCode,
  aploraTranscript,
  IAploraTranscript,
} from './aploraModel';
import { useLeadPermissions } from './useLeadPermissions';

/**
 * ТЗ 0117 — аналитика звонков Aplora в карточке лида: копия карточки сделки,
 * живой разбор звонка, транскрипт по кнопке и AI-черновик письма.
 */

export type ILeadAplora = NonNullable<
  NonNullable<LeadAploraCardQuery['kommoLead']>['aplora']
>;
export type ILeadAploraCall = ILeadAplora['calls'][number];
export type ILeadAploraCallReview = NonNullable<
  LeadAploraCallReviewQuery['aploraCallAnalysis']
>;

export interface IUseLeadAplora {
  aplora: ILeadAplora | null;
  loading: boolean;
  failed: boolean;
  /** Ответ пришёл, а копии нет: Aplora выключена или сделки в ней нет. */
  absent: boolean;
  reload: () => void;
}

/**
 * Копия карточки сделки. Запрашивается при открытии окна лида (сервер
 * перечитывает Aplora вживую, если копия старше 10 минут) — по ответу окно
 * решает, показывать ли вкладку.
 */
export const useLeadAplora = (leadId: string | null): IUseLeadAplora => {
  const { canUse } = useLeadPermissions();
  const skip = !canUse || !leadId;
  const { data, loading, error, refetch } = useLeadAploraCardQuery({
    variables: { id: leadId || '' },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const aplora = data?.kommoLead?.aplora ?? null;
  const reload = useCallback(() => {
    if (!skip) refetch()?.catch(() => undefined);
  }, [refetch, skip]);
  return {
    aplora,
    loading: loading && !data,
    failed: Boolean(error) && !data,
    absent: Boolean(data) && !aplora,
    reload,
  };
};

export interface IUseAploraCallReview {
  review: ILeadAploraCallReview | null;
  loading: boolean;
  /** Код отказа; `aplora_disabled` — ответ пустой (Aplora выключена). */
  errorCode: AploraErrorCode | null;
  reload: () => void;
}

/** Разбор звонка вживую — когда звонок раскрыт; без кэша Apollo. */
export const useAploraCallReview = (
  leadId: string,
  callId: string,
  enabled: boolean,
): IUseAploraCallReview => {
  const { data, loading, error, refetch } = useLeadAploraCallReviewQuery({
    variables: { leadId, callId },
    skip: !enabled,
    errorPolicy: 'all',
    // Разбор — живой текст Aplora: в кэше Apollo он жил бы до закрытия
    // вкладки браузера и не обновлялся после переразбора. Раскрыли — спросили
    // заново; пока раскрыт, ответ живёт в состоянии хука.
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });
  const review = data?.aploraCallAnalysis ?? null;
  const reload = useCallback(() => {
    refetch()?.catch(() => undefined);
  }, [refetch]);
  let errorCode = aploraQueryErrorCode(error);
  if (!errorCode && !loading && data && !review) errorCode = 'aplora_disabled';
  return { review, loading, errorCode: review ? null : errorCode, reload };
};

export interface IUseAploraTranscript {
  transcript: IAploraTranscript | null;
  loading: boolean;
  errorCode: AploraErrorCode | null;
  load: () => void;
}

/**
 * Транскрипт звонка по кнопке. Не кэшируется (`no-cache`): в кэше Apollo
 * он жил бы до закрытия вкладки браузера.
 */
export const useAploraTranscript = (
  leadId: string,
  callId: string,
): IUseAploraTranscript => {
  const [
    run,
    { data, loading, error, called },
  ] = useLeadAploraCallTextLazyQuery({
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });
  const raw = data?.aploraCallTranscript ?? null;
  const load = useCallback(() => {
    run({ variables: { leadId, callId } });
  }, [run, leadId, callId]);
  let errorCode = aploraQueryErrorCode(error);
  if (!errorCode && called && !loading && !raw) errorCode = 'aplora_disabled';
  return {
    transcript: raw ? aploraTranscript(raw.transcript) : null,
    loading,
    errorCode: raw ? null : errorCode,
    load,
  };
};

export interface IAploraDraft {
  subject: string;
  body: string;
  cached: boolean;
}

export interface IAploraDraftResult {
  draft: IAploraDraft | null;
  errorCode: AploraErrorCode | null;
}

/**
 * AI-черновик письма по разобранному звонку. Ключ запроса — на звонок:
 * повторный клик по тому же звонку несёт тот же ключ (сервер вернёт тот же
 * черновик, а не сгенерирует второй); «Сгенерировать заново» — новый ключ и
 * `regenerate: true`.
 */
export const useAploraFollowupDraft = (leadId: string) => {
  const [mutate, { loading }] = useDraftLeadAploraFollowupMutation();
  const keys = useRef<Record<string, string>>({});

  const generate = useCallback(
    async (callId: string, regenerate = false): Promise<IAploraDraftResult> => {
      if (regenerate || !keys.current[callId]) {
        keys.current[callId] = newSmsRequestKey();
      }
      try {
        const { data } = await mutate({
          variables: {
            leadId,
            callId,
            regenerate,
            requestKey: keys.current[callId],
          },
        });
        const payload = data?.aploraFollowupDraft;
        const draft = payload?.draft;
        if (payload?.success && draft) {
          return {
            draft: {
              subject: draft.emailSubject,
              body: draft.emailBody,
              cached: draft.cached,
            },
            errorCode: null,
          };
        }
        const code = payload?.customErrors?.find(Boolean)?.code;
        return { draft: null, errorCode: aploraErrorCode(code) };
      } catch (caught) {
        return { draft: null, errorCode: 'aplora_unavailable' };
      }
    },
    [leadId, mutate],
  );

  return { generate, generating: loading };
};
